import { getCapabilities, type PACapabilities } from '../api/access';
import { usePAQuery, type PAQueryResult } from '../money/usePAQuery';

/**
 * useCapabilities reads `GET /pa/accounts/capabilities` — «what does THIS installation let
 * a subscriber do, and from which dates».
 *
 * ============================================================================
 * ⚠ WHY IT IS A HOOK PER SCREEN AND NOT A PROVIDER
 * ============================================================================
 *
 * Four surfaces need it (the activation button, the two block forms, the account-password
 * form, the de-registration dialog's wording), so a context was the tempting shape. It is
 * deliberately NOT one, and the reason is a measured hazard of this codebase rather than a
 * preference: a provider's POSITION in the tree can be deleted and the suite stays green
 * whenever the property under test depends on a child READING the value — so the one thing
 * a provider would buy (one request instead of four) would be bought with a wiring fact no
 * test can express. Each screen is a page load; four reads across four page loads is not a
 * cost worth a guard nobody can falsify.
 *
 * ============================================================================
 * ⚠ AND WHY A FAILED READ MEANS «NOT PERMITTED» AND NEVER «PROBABLY PERMITTED»
 * ============================================================================
 *
 * `capabilityOr` exists so a screen can fail CLOSED in one line. Three of these four
 * capabilities are behind an operator switch and the write routes enforce every one of
 * them, so an unknown answer offering the control produces a button whose only possible
 * outcome is a `409` the subscriber cannot act on — the dead-control class, and worse than
 * usual because he cannot ask a colleague what it means. The server itself takes the same
 * direction: it reports `self_activation_enabled: false` when it cannot read the setting.
 *
 * The two screens whose whole purpose is one of these controls (the block forms, the
 * account password) do NOT use `capabilityOr`: they render the query's error frame with
 * «Повторить», because a screen that silently showed «нельзя» for a transient read failure
 * would be telling the subscriber his operator had switched something off.
 */
export function useCapabilities(): PAQueryResult<PACapabilities> {
  return usePAQuery<PACapabilities>(() => getCapabilities(), []);
}

/**
 * capabilityOr reads one capability out of a query that may be loading or failed, with an
 * explicit fallback — the FAIL-CLOSED value at every call site that offers a control.
 */
export function capabilityOr<K extends keyof PACapabilities>(
  query: PAQueryResult<PACapabilities>['query'],
  key: K,
  fallback: PACapabilities[K],
): PACapabilities[K] {
  return query.status === 'ready' ? query.data[key] : fallback;
}
